import Api from "../../../../../../../../core/services/api/api";
import Utils from "../../../../../../../../core/services/utils/utils";
import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer, toPartNumberUpper} from "./shared";

/**
 * Part online order eligibility import — phased validation (see {@link BaseImportTransformer} run contract).
 */
export class PartOnlineOrderEligibilityImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     */
    async run(mappedData, isFinalRun = false) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, partNoUpper: string, codes: string[]}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item,
            partNoUpper: toPartNumberUpper(item?.partNumber),
            codes: [],
        }));

        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!rs.partNoUpper) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        const uniqueParts = [...new Set(rowState.map((r) => r.partNoUpper).filter(Boolean))].sort();
        /** @type {Record<string, *>} */
        let partIds = {};
        if (uniqueParts.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(uniqueParts);
            partIds = bundle.partIds;
        }

        for (const rs of rowState) {
            if (!rs.item) continue;
            if (rs.partNoUpper && !partIds[rs.partNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        for (const rs of rowState) {
            if (!rs.item) continue;
            const eligibleForOnlineOrdering = Utils.tryConvertStringToBoolean(rs.item.eligibleForOnlineOrdering);
            if (eligibleForOnlineOrdering === undefined) {
                rs.codes.push(PreventionInvalidityCode.INVALID_BOOLEAN);
            }
        }

        /** @type {(null|{ eligibleForOnlineOrdering: boolean, response: * })[]} */
        const keyInfoSlotByRowIndex = new Array(rows.length).fill(null);
        await Promise.all(
            rowState.map(async (rs, index) => {
                if (!rs.item || !rs.partNoUpper) return;
                const partId = partIds[rs.partNoUpper];
                if (!partId) return;

                const eligibleForOnlineOrdering = Utils.tryConvertStringToBoolean(rs.item.eligibleForOnlineOrdering);
                if (eligibleForOnlineOrdering === undefined) return;

                const keyInfoResponse = await Api.getProductKeyInformation(rs.partNoUpper);
                keyInfoSlotByRowIndex[index] = {eligibleForOnlineOrdering, response: keyInfoResponse};
            }),
        );

        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.INVALID_PART],
                });
                continue;
            }
            if (!rs.codes.length) continue;
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, index) => {
            if (!item) {
                return isFinalRun ? null : {partId: null, partNumber: undefined};
            }

            const rs = rowState[index];
            if (!rs.partNoUpper) {
                return isFinalRun ? null : {partId: null, partNumber: item.partNumber};
            }

            const partId = partIds[rs.partNoUpper];
            const hasIssue = issueIndexSet.has(index);

            if (hasIssue) {
                return isFinalRun
                    ? null
                    : {partId: partId ?? null, partNumber: item.partNumber};
            }

            const slot = keyInfoSlotByRowIndex[index];
            const currentEligible = slot?.response?.data?.isEligibleForOnlineOrdering !== false;
            if (
                !slot?.response?.resultFlag
                || currentEligible === slot.eligibleForOnlineOrdering
            ) {
                return null;
            }

            return {
                partId,
                isEligibleForOnlineOrdering: slot.eligibleForOnlineOrdering,
            };
        });

        return {apiRequests, preventativeIssues};
    }
}
